/**
 * Built-in developmental framework data — literacy, language, logic.
 *
 * Every level descriptor names a behavior a parent can watch for in the moment.
 * "Understands patterns" is not assessable; "continues an AB pattern of six items
 * and builds her own" is.
 *
 * Research anchors, by skill family:
 *  - Reading: Ehri's phases of sight word learning (pre alphabetic → partial →
 *    full → consolidated), Chall's stages (decoding, then confirmation and
 *    fluency), LaBerge and Samuels on automaticity, Samuels on repeated reading,
 *    Clay's Concepts About Print for print awareness.
 *  - Language: Brown's morpheme and MLU stages, Applebee and Stein and Glenn on
 *    story structure, Vygotsky on private speech becoming inner planning.
 *  - Logic: Piaget on classification, class inclusion, seriation and
 *    transitivity; Goswami on early relational analogy; Hawkins and colleagues
 *    on syllogisms with fantasy premises; Raven style matrices.
 *
 * On `typicalMonths`: it names the age band in which level 3 is ordinary for
 * THAT skill, not a global band. Foundational skills sit near 30 to 36 months,
 * matching LEVEL_AGE_BANDS. Skills that cannot begin that early — reading
 * fluency, syllogisms, designing a puzzle for someone else — carry a later band,
 * so their ladder stays honest instead of being compressed into infancy. It is
 * gentle guidance for the planner. It is never shown as a shortfall.
 */

import type {
  DomainDef,
  DomainId,
  DomainKey,
  FrameworkId,
  Level,
  LevelDescriptor,
  Skill,
  SkillId,
} from '../types'

// ─────────────────────────────────────────────────────────────
// Ids
// ─────────────────────────────────────────────────────────────

const FRAMEWORK_ID = 'framework.aurora.builtin.v1' as FrameworkId

const domainId = (key: DomainKey): DomainId => `domain.${key}` as DomainId

const skillId = (domainKey: DomainKey, key: string): SkillId =>
  `${domainKey}.${key}` as SkillId

// ─────────────────────────────────────────────────────────────
// Authoring helpers
// ─────────────────────────────────────────────────────────────

/** One rung of the ladder. Level number comes from position. */
interface Rung {
  readonly descriptor: string
  readonly exemplars: readonly string[]
}

/** Exactly five rungs, level 1 through level 5. */
type Ladder = readonly [Rung, Rung, Rung, Rung, Rung]

interface SkillSeed {
  readonly key: string
  readonly name: string
  readonly description: string
  readonly typicalMonths?: readonly [number, number]
  /** Fully qualified skill keys, e.g. "logic.categories". */
  readonly prerequisites: readonly string[]
  readonly assessmentHints: readonly string[]
  readonly ladder: Ladder
}

const toLevelDescriptors = (ladder: Ladder): LevelDescriptor[] =>
  ladder.map((rung, index) => ({
    level: (index + 1) as Level,
    descriptor: rung.descriptor,
    exemplars: [...rung.exemplars],
  }))

const buildSkills = (domainKey: DomainKey, seeds: readonly SkillSeed[]): Skill[] =>
  seeds.map((seed, index) => ({
    _id: skillId(domainKey, seed.key),
    domainId: domainId(domainKey),
    domainKey,
    key: seed.key,
    name: seed.name,
    description: seed.description,
    levelDescriptors: toLevelDescriptors(seed.ladder),
    ...(seed.typicalMonths ? { typicalMonths: seed.typicalMonths } : {}),
    prerequisites: seed.prerequisites.map((id) => id as SkillId),
    assessmentHints: [...seed.assessmentHints],
    sortOrder: index + 1,
  }))

// ─────────────────────────────────────────────────────────────
// Domains
// ─────────────────────────────────────────────────────────────

export const LITERACY_DOMAIN: DomainDef = {
  _id: domainId('literacy'),
  frameworkId: FRAMEWORK_ID,
  key: 'literacy',
  name: 'Literacy & Reading',
  description:
    'Turning print into meaning: letter sounds, blending, decoding, and the separate work of making decoding automatic enough that a story flows.',
  colorToken: 'domain.literacy',
  iconKey: 'book-open',
  sortOrder: 1,
}

export const LANGUAGE_DOMAIN: DomainDef = {
  _id: domainId('language'),
  frameworkId: FRAMEWORK_ID,
  key: 'language',
  name: 'Language & Communication',
  description:
    'Words, sentences, and stories out loud: the vocabulary she has, the grammar she can reach, and how well she makes another person understand her.',
  colorToken: 'domain.language',
  iconKey: 'messages-square',
  sortOrder: 2,
}

export const LOGIC_DOMAIN: DomainDef = {
  _id: domainId('logic'),
  frameworkId: FRAMEWORK_ID,
  key: 'logic',
  name: 'Logic & Reasoning',
  description:
    'Finding the rule and using it: opposites, causes, categories, analogies, and reasoning that reaches a conclusion she was never told.',
  colorToken: 'domain.logic',
  iconKey: 'puzzle',
  sortOrder: 3,
}

export const LITERACY_LANGUAGE_LOGIC_DOMAINS: DomainDef[] = [
  LITERACY_DOMAIN,
  LANGUAGE_DOMAIN,
  LOGIC_DOMAIN,
]

// ─────────────────────────────────────────────────────────────
// Literacy
//
// The organizing idea: accuracy and automaticity are different skills.
// A child can read every word correctly and still be at level 1 in fluency,
// because decoding is eating all of her attention. Splitting them is what
// lets the system see a fluency wall instead of a strong reader.
// ─────────────────────────────────────────────────────────────

const LITERACY_SEEDS: readonly SkillSeed[] = [
  {
    key: 'letterSounds',
    name: 'Letter Sounds',
    description:
      'The sound each letter makes, produced on sight. The raw material every other reading skill spends.',
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'Shuffle a small deck of letter cards during play and ask "what sound?" for five of them, then stop while it is still fun.',
      'Point at a sign or a cereal box and ask what sound the first letter makes.',
      'Play "I spy something starting with /f/" in the car and see whether she works from the sound or the letter name.',
    ],
    ladder: [
      {
        descriptor:
          'Treats letters as special marks rather than pictures. Joins the alphabet song and finds the first letter of her own name.',
        exemplars: [
          'Points at the A on a magnet board and says "that\'s mine".',
          'Sings the alphabet song with several letters clear.',
        ],
      },
      {
        descriptor:
          'Produces the right sound for three to five letters, usually the ones in her name and on favorite packages.',
        exemplars: [
          'Says /m/ for M and /s/ for S every time, and guesses at the rest.',
          'Recognizes the letters of her name out of order.',
        ],
      },
      {
        descriptor:
          'Gives the correct sound for eight to twelve letters shown in random order, without being told the letter name first.',
        exemplars: [
          'Gets ten of a shuffled twelve card deck on a good day.',
          'Names the starting sound of a word she hears about half the time.',
        ],
      },
      {
        descriptor:
          'Gives the correct sound for twenty or more single letters on sight, and can say what sound a spoken word starts with.',
        exemplars: [
          'Runs a whole alphabet deck with only the tricky ones (q, x, y) wobbling.',
          'Says "sun starts with /s/" without seeing the word.',
        ],
      },
      {
        descriptor:
          'All twenty six sounds instantly in shuffled order, plus the letter teams sh, ch, th and ck, and both sounds of c and g.',
        exemplars: [
          'Observed: all 26 sounds known and used while reading.',
          'Points at the ck in "snack" and says it makes one sound, /k/.',
        ],
      },
    ],
  },
  {
    key: 'blending',
    name: 'Sound Blending',
    description:
      'Pushing separate sounds together into a whole word. The hinge that turns letter knowledge into reading.',
    typicalMonths: [42, 54],
    prerequisites: ['literacy.letterSounds'],
    assessmentHints: [
      'Say a word in slow robot talk at bath time (/c/ /u/ /p/) and let her guess it before you hand it over.',
      'Say the sounds of the snack she asked for and see whether she blends them back before she eats it.',
      'Listen for whether she keeps a cluster together (sn, sw) or breaks it into two separate sounds.',
    ],
    ladder: [
      {
        descriptor:
          'Hears the beat of words: claps syllables in a name and joins in on a rhyme she knows.',
        exemplars: [
          'Claps AU RO RA in three beats.',
          'Fills in the rhyming word at the end of a familiar line.',
        ],
      },
      {
        descriptor:
          'Blends two spoken sounds into a word when an adult holds each one long: /m/ + /e/ becomes "me".',
        exemplars: [
          'Blends "up", "on", "at" when the sounds are stretched.',
          'Blends the first sound onto the rest: /c/ + "at" gives "cat".',
        ],
      },
      {
        descriptor:
          'Blends three spoken sounds into a simple word with no letters in front of her: /c/ /a/ /t/ becomes "cat".',
        exemplars: [
          'Guesses the hidden toy from its sounds, /d/ /o/ /g/.',
          'Blends four out of five words in a robot talk game.',
        ],
      },
      {
        descriptor:
          'Blends a printed word by herself: sounds it out along the letters, then says the whole word together.',
        exemplars: [
          'Sounds out "s a t" then says "sat" without help.',
          'Goes back and reblends when the first attempt sounds wrong.',
        ],
      },
      {
        descriptor:
          'Blends words with consonant clusters and letter teams, holding the cluster as one chunk rather than splitting it.',
        exemplars: [
          'Observed: reads "snacks" and "swift", keeping sn and sw together.',
          'Reads "shop" and "duck" treating sh and ck as single sounds.',
        ],
      },
    ],
  },
  {
    key: 'decodingAccuracy',
    name: 'Decoding Accuracy',
    description:
      'Reading an unfamiliar printed word correctly by working through its letters. Accuracy only. How fast she does it is a different skill.',
    typicalMonths: [54, 66],
    prerequisites: ['literacy.letterSounds', 'literacy.blending'],
    assessmentHints: [
      'Write two or three words on sticky notes and hide them around the room as a treasure hunt.',
      'When she meets a new word in a book, wait five seconds before helping and note what she does with the time.',
      'Watch for self correction: does she stop when what she read is not a real word?',
    ],
    ladder: [
      {
        descriptor:
          'Recognizes a few whole words by their shape or logo and reads a memorized book from the pictures.',
        exemplars: [
          'Reads the STOP sign and her own name.',
          'Turns pages of a known book and says the line from memory.',
        ],
      },
      {
        descriptor:
          'Uses the first letter plus the picture to land on a word. Right when the picture helps, guessy when it does not.',
        exemplars: [
          'Reads "dog" on a page with a dog on it; says "puppy" when the picture is missing.',
          'Knows the word starts with /b/ before she knows the word.',
        ],
      },
      {
        descriptor:
          'Sounds out a printed three sound word correctly (cat, sun, big) when an adult keeps the pace steady.',
        exemplars: [
          'Reads four of five word cards correctly with encouragement.',
          'Gets the word right after one restart.',
        ],
      },
      {
        descriptor:
          'Reads three and four sound words correctly on her own, including ck, sh and th endings, and self corrects a word that came out wrong.',
        exemplars: [
          'Reads "duck", "fish", "bath" unaided.',
          'Says "wait, that\'s not a word" and tries again.',
        ],
      },
      {
        descriptor:
          'Reads words with beginning clusters and common vowel teams correctly, and works through a brand new word instead of asking for it.',
        exemplars: [
          'Observed: reads "snacks" and "swift" accurately.',
          'Takes on an unfamiliar word on a menu and gets it.',
        ],
      },
    ],
  },
  {
    key: 'sightWordAutomaticity',
    name: 'Sight Word Automaticity',
    description:
      'Reading the most common words instantly, with no sounding out, so decoding effort is free for the words that actually need it.',
    typicalMonths: [60, 72],
    prerequisites: ['literacy.letterSounds'],
    assessmentHints: [
      'Flip ten word cards at a steady pace and keep the ones she reads within about a second in their own pile.',
      'Notice which words she stops to sound out inside a story. Those are the ones worth practicing.',
      'Tape words around the house and have her read whichever one she reaches.',
    ],
    ladder: [
      {
        descriptor:
          'Recognizes her own name and two or three words in the places she always sees them.',
        exemplars: [
          'Picks her name off a coat hook.',
          'Reads the word on a favorite cereal box.',
        ],
      },
      {
        descriptor:
          'Reads about ten common words from a card in a second or two, sometimes needing the first letter pointed at.',
        exemplars: [
          'Knows the, and, I, my, go on a card.',
          'Gets "is" once you cover the rest of the sentence.',
        ],
      },
      {
        descriptor:
          'Reads twenty five to forty common words instantly from a shuffled deck, with no sounding out.',
        exemplars: [
          'Runs a thirty card deck with only a few pauses.',
          'Reads "you", "was", "for" as fast as she reads her name.',
        ],
      },
      {
        descriptor:
          'Reads the top hundred words instantly, including the ones that do not sound out: said, come, one, of.',
        exemplars: [
          'Reads "said" without trying to sound it.',
          'Recognizes a hundred word deck in a couple of minutes.',
        ],
      },
      {
        descriptor:
          'Never stops on a common word inside a sentence, and can point one out inside a line of text.',
        exemplars: [
          'Reads a whole page pausing only on new content words.',
          'Finds every "the" on a page in a few seconds.',
        ],
      },
    ],
  },
  {
    key: 'decodingFluency',
    name: 'Reading Fluency',
    description:
      'How automatic decoding has become. Same words, less effort: reading in phrases at a speed that leaves attention over for meaning. A child can be excellent at accuracy and still be starting out here.',
    typicalMonths: [66, 78],
    prerequisites: ['literacy.decodingAccuracy', 'literacy.sightWordAutomaticity'],
    assessmentHints: [
      'Read the same easy page three times with a sand timer. The change from the first read to the third is the signal, not the raw time.',
      'Listen to the phrasing: is her voice grouping two or three words together, or is there a pause before every word?',
      'Right after she reads a sentence, ask what it said. If the meaning slipped away, decoding is still taking all the attention.',
      'Try a page one full step easier than usual and notice how much smoother it sounds.',
    ],
    ladder: [
      {
        descriptor:
          'Reads word by word with a pause before each one. Every word is a fresh problem, and the sentence meaning is gone by the end of it.',
        exemplars: [
          'Observed: decodes "snacks" correctly, and the six word sentence takes about twenty seconds.',
          'Reads accurately and then cannot say what the line was about.',
        ],
      },
      {
        descriptor:
          'A second and third read of the same page is noticeably smoother than the first. A first read is still one word at a time.',
        exemplars: [
          'Third pass at a Bob Books page beats the first by half.',
          'Repeats a familiar line at speed, then slows right down on the next line.',
        ],
      },
      {
        descriptor:
          'Reads a practiced easy page in phrases, grouping two or three words with her voice. A fresh page returns to word by word.',
        exemplars: [
          'Reads "the cat sat" as one phrase instead of three words.',
          'Sounds like talking on the rehearsed page.',
        ],
      },
      {
        descriptor:
          'Reads an unpracticed easy page smoothly at roughly thirty to fifty words a minute, stopping at periods and lifting her voice at question marks.',
        exemplars: [
          'Reads a new Elephant and Piggie page in one pass.',
          'Pauses at the comma without being told.',
        ],
      },
      {
        descriptor:
          'Reads new early reader text at talking speed, gives characters different voices, and answers a question about it right away.',
        exemplars: [
          'Reads a page aloud and immediately says what the character wanted.',
          'Uses a growly voice for the bear without slowing down.',
        ],
      },
    ],
  },
  {
    key: 'comprehension',
    name: 'Reading Comprehension',
    description:
      'Getting meaning from a story: what happened, who wanted what, and why. Counts whether the story was read to her or by her.',
    typicalMonths: [36, 48],
    prerequisites: ['language.vocabulary'],
    assessmentHints: [
      'After a story, ask what the problem was and how it got fixed.',
      'Stop one page before the end and ask what she thinks happens next, and why she thinks so.',
      'Ask a why question about a character\'s choice rather than a what question about the events.',
    ],
    ladder: [
      {
        descriptor:
          'Points to and names things in the pictures when asked where something is.',
        exemplars: [
          'Finds the moon on the page when asked.',
          'Names the animals as you read.',
        ],
      },
      {
        descriptor:
          'Answers who and what happened about a story just heard, in a few words.',
        exemplars: [
          'Says "the bear ate it" about the page you just read.',
          'Names the main character after one reading.',
        ],
      },
      {
        descriptor:
          'Retells a familiar story in order, beginning to middle to end, and names what went wrong in it.',
        exemplars: [
          'Observed: retells and answers problem and solution questions.',
          'Says "the balloon flew away, and then they got a new one".',
        ],
      },
      {
        descriptor:
          'Says what a character wanted and why they did what they did, and predicts the next event before the page turns.',
        exemplars: [
          'Says "he hid because he was scared of the dog".',
          'Guesses the ending and gives a reason for the guess.',
        ],
      },
      {
        descriptor:
          'Explains how the problem got solved, links the story to something in her own life, and answers a question about text she decoded herself.',
        exemplars: [
          'Says "that\'s like when I lost my hat at the park".',
          'Reads a page alone and then explains what happened in it.',
        ],
      },
    ],
  },
  {
    key: 'printAwareness',
    name: 'Print Awareness',
    description:
      'How print works on a page: that the marks carry the message, which way they run, and where one word stops and the next begins.',
    typicalMonths: [36, 48],
    prerequisites: [],
    assessmentHints: [
      'Hand her a book upside down and say nothing. Watch what she does with it.',
      'Ask her to point at the words while you read a line she knows by heart.',
      'Ask her to show you one word, then one letter, then the whole sentence.',
    ],
    ladder: [
      {
        descriptor:
          'Holds a book the right way up, turns pages one at a time, and pats or names the pictures.',
        exemplars: [
          'Flips the book around when handed it upside down.',
          'Turns single pages rather than handfuls.',
        ],
      },
      {
        descriptor:
          'Knows the front of the book and where a story starts, and asks what the writing says.',
        exemplars: [
          'Opens to the first page rather than the middle.',
          'Points at a label and asks "what does that say?"',
        ],
      },
      {
        descriptor:
          'Sweeps a finger left to right and down the page, and knows the print, not the picture, is the part being read.',
        exemplars: [
          'Runs her finger along the line as you read.',
          'Says the words come from the letters when asked.',
        ],
      },
      {
        descriptor:
          'Matches one printed word to one spoken word on a familiar line, and shows the first and last letter of a word.',
        exemplars: [
          'Lands on the last word exactly as she says it.',
          'Covers the last letter of "cat" when asked.',
        ],
      },
      {
        descriptor:
          'Tells a letter, a word and a sentence apart, and notices a period, a question mark and a capital letter and says what each one does.',
        exemplars: [
          'Says the question mark means you say it like a question.',
          'Points out that the sentence starts with a big letter.',
        ],
      },
    ],
  },
  {
    key: 'independentReading',
    name: 'Independent Reading',
    description:
      'Choosing to read on her own, on paper, for her own reasons. This is what all the other literacy skills are for.',
    typicalMonths: [66, 78],
    prerequisites: ['literacy.decodingFluency', 'literacy.comprehension'],
    assessmentHints: [
      'Leave three very easy books where she plays and see what she does with them when nobody suggests anything.',
      'Ask her to read to a doll, a baby or the dog. An audience she is caring for changes everything.',
      'Notice whether a paper book gets picked up at any point during a free hour.',
    ],
    ladder: [
      {
        descriptor:
          'Sits alone with a book and retells it page by page from the pictures and memory.',
        exemplars: [
          'Reads a known book to herself in the corner.',
          'Uses story voice even though she is not reading the words.',
        ],
      },
      {
        descriptor:
          'Reads along where the words are carried for her: an app, an audio book, or the repeated line in a picture book.',
        exemplars: [
          'Observed: reads short stories daily on the iPad app.',
          'Joins in on every repeated refrain.',
        ],
      },
      {
        descriptor:
          'Reads a very easy paper book she has heard many times to an adult or a doll, needing help on about one word a page.',
        exemplars: [
          'Reads a Bob Books page to the baby.',
          'Gets through a familiar book with three assists.',
        ],
      },
      {
        descriptor:
          'Picks up an unfamiliar easy reader and gets through it alone, asking for a word only now and then.',
        exemplars: [
          'Finishes a new easy reader in one sitting.',
          'Asks for one word in eight pages.',
        ],
      },
      {
        descriptor:
          'Reads paper books for pleasure without being asked, several in a sitting, and talks about them afterwards.',
        exemplars: [
          'Found reading in bed with a stack she chose.',
          'Brings a book over to tell you the funny part.',
        ],
      },
    ],
  },
]

export const LITERACY_SKILLS: Skill[] = buildSkills('literacy', LITERACY_SEEDS)

// ─────────────────────────────────────────────────────────────
// Language
// ─────────────────────────────────────────────────────────────

const LANGUAGE_SEEDS: readonly SkillSeed[] = [
  {
    key: 'vocabulary',
    name: 'Vocabulary',
    description:
      'How many words she understands and uses, and how exact they are. The strongest single predictor of later reading comprehension.',
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'Use a word from yesterday\'s story in conversation today and see whether she takes it up.',
      'Ask "what is a ___?" about a word she just used and listen to how she explains it.',
      'Keep a note on your phone of new words she says in a week. The list is the measure.',
    ],
    ladder: [
      {
        descriptor:
          'Uses about fifty single words and understands many more. Points at things to get them named.',
        exemplars: [
          'Says milk, more, dog, up, mine.',
          'Fetches the right object out of three when it is named.',
        ],
      },
      {
        descriptor:
          'Uses roughly two to three hundred words, including action words and describing words, and asks for the names of things.',
        exemplars: [
          'Uses jump, sleepy, dirty, tomorrow.',
          'Asks "what that called?" several times a day.',
        ],
      },
      {
        descriptor:
          'Uses five hundred or more words, picks up new ones daily, and uses group words like animal, vehicle and food.',
        exemplars: [
          'Observed: 500+ words, learning new ones every day.',
          'Says "those are all vehicles" about the toy bin.',
        ],
      },
      {
        descriptor:
          'Reaches for the exact word instead of the general one, and asks what an unfamiliar word means.',
        exemplars: [
          'Says poodle rather than dog, enormous rather than big.',
          'Stops a story to ask what "ingredient" means.',
        ],
      },
      {
        descriptor:
          'Uses and explains words picked up from books and adults, and gives a definition or an example when asked what one means.',
        exemplars: [
          'Uses magnifier, experiment, invisible in the right places.',
          'Explains that a prediction is "what you think will happen".',
        ],
      },
    ],
  },
  {
    key: 'sentenceComplexity',
    name: 'Sentence Complexity',
    description:
      'The grammatical reach of her speech: length, tense, and the connecting words that join two ideas into one sentence.',
    typicalMonths: [30, 36],
    prerequisites: ['language.vocabulary'],
    assessmentHints: [
      'Write down three of her sentences word for word at dinner, once a week. The growth shows on paper.',
      'Listen for because, so and when. The connecting words are the growing edge, not the length.',
      'Notice regularized past tense like "goed" and "runned". That is the rule being learned, not a slip.',
    ],
    ladder: [
      {
        descriptor: 'Puts two words together to make a request or a comment.',
        exemplars: ['"More milk."', '"Daddy go."'],
      },
      {
        descriptor:
          'Three and four word sentences in the right order, with plurals and the -ing ending appearing.',
        exemplars: ['"I making a cake."', '"The dogs are running."'],
      },
      {
        descriptor:
          'Sentences of five words or more, joined with and or but, and past tense in use, sometimes regularized.',
        exemplars: [
          'Observed: 5+ word sentences with connectors.',
          '"We goed to the park and I runned fast."',
        ],
      },
      {
        descriptor:
          'Joins two ideas with because, so or when, uses a future form, and forms negatives and questions correctly.',
        exemplars: [
          '"I put my coat on because it\'s cold outside."',
          '"We\'re going to see grandma when it\'s Saturday."',
        ],
      },
      {
        descriptor:
          'Uses if, before and after, and tucks one clause inside another. Irregular past tense mostly correct.',
        exemplars: [
          '"The cat that I drew is sleeping in the house I made."',
          '"Before we go, I have to find my shoes."',
        ],
      },
    ],
  },
  {
    key: 'narration',
    name: 'Narration',
    description:
      'Talking through what she is doing as she does it. Vygotsky called this private speech: out loud first, then it moves inside and becomes planning.',
    typicalMonths: [30, 36],
    prerequisites: ['language.sentenceComplexity'],
    assessmentHints: [
      'Sit near her play and stay quiet for two minutes. Listen to what she says when she is not talking to you.',
      'Ask her to tell you what she is making while she is making it.',
      'Hand her a toy microphone and let her commentate her own game.',
    ],
    ladder: [
      {
        descriptor: 'Names objects and actions as she plays, a word at a time.',
        exemplars: ['"Car. Go. Crash."', 'Labels each animal as she lines them up.'],
      },
      {
        descriptor: 'Short sentences about what is happening right now.',
        exemplars: ['"I making cake."', '"Baby sleeping now."'],
      },
      {
        descriptor:
          'Keeps up a running commentary through several minutes of play, including what the toys are doing and saying.',
        exemplars: [
          'Observed: constant running commentary during play.',
          'Gives two dolls a conversation while she builds.',
        ],
      },
      {
        descriptor:
          'Talks herself through a task step by step and plans out loud before she acts.',
        exemplars: [
          '"First I need the glue, then the paper, then the sparkles."',
          '"That won\'t work. I need a bigger one."',
        ],
      },
      {
        descriptor:
          'Narrates for a listener: gives enough background that someone who just walked in can follow, and switches voices for different characters.',
        exemplars: [
          '"This is the mama and she lost her baby, and now she\'s looking."',
          'Uses a squeaky voice for the mouse and a low one for the bear.',
        ],
      },
    ],
  },
  {
    key: 'questionAsking',
    name: 'Question Asking',
    description:
      'Using questions to get what she needs to know, and what kind of information she goes after.',
    typicalMonths: [30, 36],
    prerequisites: ['language.sentenceComplexity'],
    assessmentHints: [
      'Answer a why question and then stay quiet. Does a follow up arrive?',
      'Put something odd on the table and say nothing. Count the questions.',
      'Notice when a question is testing an idea she already has rather than asking for a fact.',
    ],
    ladder: [
      {
        descriptor:
          'Asks by pointing with a rising voice, or with a single question word.',
        exemplars: ['Points at the sky and says "what that?"', 'Lifts her voice to turn a word into a question.'],
      },
      {
        descriptor: 'Asks where and who questions and waits for the answer.',
        exemplars: ['"Where daddy go?"', '"Who made this?"'],
      },
      {
        descriptor:
          'Asks why and how questions many times a day and listens to the answer instead of moving on.',
        exemplars: [
          'Observed: uses what, where, why, how and when.',
          '"How does the water get in the pipe?"',
        ],
      },
      {
        descriptor:
          'Asks a follow up on the answer she just got, going two or three questions deep on one topic.',
        exemplars: [
          '"Why is it raining?" then "where does the rain come from?" then "how does it get up there?"',
          'Asks what a word in your answer means.',
        ],
      },
      {
        descriptor:
          'Asks a question to test an idea she has, and asks for clarification when something does not add up.',
        exemplars: [
          '"Does it still float if we put a rock in it?"',
          '"But you said it was closed. Why is the light on?"',
        ],
      },
    ],
  },
  {
    key: 'instructionFollowing',
    name: 'Instruction Following',
    description:
      'Holding spoken directions in mind and carrying them out in order.',
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'Give a three step direction once, in a normal voice, from across the room. Watch the order she does them in.',
      'Ask her to say the plan back before she starts.',
      'Try one with a condition in it: if the towel is wet, bring the other one.',
    ],
    ladder: [
      {
        descriptor: 'Follows a one step direction that comes with a gesture.',
        exemplars: ['Gives you the ball when you ask and hold out a hand.', 'Comes when called and beckoned.'],
      },
      {
        descriptor:
          'Follows a one step direction with no gesture, and a two step routine she knows well.',
        exemplars: ['"Get your shoes" from the next room.', 'Does the whole handwashing routine on the word.'],
      },
      {
        descriptor:
          'Follows a new two or three step direction in the right order.',
        exemplars: [
          'Observed: follows 3+ step sequential tasks.',
          '"Get your shoes, put them by the door, then come back."',
        ],
      },
      {
        descriptor:
          'Follows three or four steps including a condition, and keeps the order when the steps are unrelated.',
        exemplars: [
          '"Put the blue one in the box, and if the lid is on the floor, bring it here."',
          'Runs a four step tidy up from one telling.',
        ],
      },
      {
        descriptor:
          'Repeats a multi step direction back before starting, and can carry it out minutes later.',
        exemplars: [
          'Says the plan back in her own words, then does it.',
          'Remembers the errand you gave her at the start of the walk.',
        ],
      },
    ],
  },
  {
    key: 'causalLanguage',
    name: 'Causal Language',
    description:
      'Saying why out loud. Because, so and if are the words that let her share reasoning instead of only having it.',
    typicalMonths: [30, 36],
    prerequisites: ['language.sentenceComplexity', 'logic.causeEffect'],
    assessmentHints: [
      'Ask why about something she just did, then wait through the pause without filling it.',
      'Ask the other direction: "the cup is empty. How did that happen?"',
      'Right after a spill or a tumble, ask what made it happen.',
    ],
    ladder: [
      {
        descriptor:
          'Shows cause and effect by acting, not by saying: pushes it to make it fall, then looks at you.',
        exemplars: ['Knocks the tower and watches your face.', 'Repeats the action to get the same result.'],
      },
      {
        descriptor:
          'Answers why with "because" plus a fragment, or by restating what happened.',
        exemplars: ['"Because I want."', '"Because it fell down."'],
      },
      {
        descriptor: 'Uses because to give a real reason for something.',
        exemplars: [
          '"I want juice because I\'m thirsty."',
          '"I put the lid on because the cat drinks it."',
        ],
      },
      {
        descriptor:
          'Uses because and so in both directions, and can give somebody else\'s reason.',
        exemplars: [
          '"It fell, so it broke." and "It broke because it fell."',
          '"She\'s crying because she wanted the red one."',
        ],
      },
      {
        descriptor:
          'Uses if and unless about something that has not happened yet, and stacks two reasons in one sentence.',
        exemplars: [
          '"If it rains we can\'t go, so we should take the umbrella."',
          '"Unless we hurry, the shop will be closed."',
        ],
      },
    ],
  },
  {
    key: 'storyRetelling',
    name: 'Story Retelling',
    description:
      'Telling back a story that was read or lived, with the parts in order and the point intact. Applebee\'s progression runs from a heap of events to a real narrative.',
    typicalMonths: [36, 48],
    prerequisites: ['language.narration', 'literacy.comprehension'],
    assessmentHints: [
      'After the bedtime story, ask her to tell it to the other parent while you listen.',
      'Ask her to tell you about her day starting from waking up, and notice whether the order holds.',
      'Put three picture cards down and ask for one story that uses all of them.',
    ],
    ladder: [
      {
        descriptor:
          'Names two or three things that happened, in any order, with no thread between them.',
        exemplars: ['"Bear. Honey. He falled."', 'Lists the pictures she liked.'],
      },
      {
        descriptor:
          'Lists events that belong together and follow each other, but with no problem and no ending.',
        exemplars: ['"They went to the park and they had ice cream and they went home."', 'Stops mid story with no closing.'],
      },
      {
        descriptor:
          'Retells with a beginning, a middle and an end, and names what went wrong in the story.',
        exemplars: [
          'Observed: retells with beginning, middle, end and names the problem.',
          '"The balloon flew away, that\'s the bad part, and then they got a new one."',
        ],
      },
      {
        descriptor:
          'Retells including what the character wanted and how the problem got fixed, keeping the same characters throughout.',
        exemplars: [
          '"He wanted the honey but the bees were there, so he waited till night."',
          'Uses the character names consistently to the end.',
        ],
      },
      {
        descriptor:
          'Tells an original story with a setting, a problem, a resolution and a real ending.',
        exemplars: [
          'Makes up a story about a lost kitten who finds her way home by following the smell.',
          'Finishes with a closing line rather than trailing off.',
        ],
      },
    ],
  },
  {
    key: 'description',
    name: 'Description',
    description:
      'Describing something well enough that a listener who cannot see it knows exactly what she means.',
    typicalMonths: [30, 36],
    prerequisites: ['language.vocabulary'],
    assessmentHints: [
      'Play "guess what I am thinking of" with the roles swapped, so she has to describe and you guess.',
      'Have her describe a toy over the phone to a grandparent who cannot see it.',
      'After she gives you a color, ask what it is for and what it is made of.',
    ],
    ladder: [
      {
        descriptor:
          'Names the thing and nothing else, and expects you to know which one she means.',
        exemplars: ['Says "ball" while pointing at it.', 'Says "that\'s a truck" with no more detail.'],
      },
      {
        descriptor:
          'Adds one feature to the name, usually a color or a size.',
        exemplars: ['"Big ball."', '"Red car."'],
      },
      {
        descriptor:
          'Uses two features together and says where a thing is with in, on or under.',
        exemplars: ['"The big red ball."', '"The little cat under the chair."'],
      },
      {
        descriptor:
          'Gives three or more features including what it is for or what it is made of, and compares two things.',
        exemplars: [
          '"It\'s a small wooden spoon for stirring the soup."',
          '"This one is heavier than that one."',
        ],
      },
      {
        descriptor:
          'Describes an object precisely enough for someone to pick it out or draw it unseen, including what it is not.',
        exemplars: [
          '"It\'s round and orange with a green stalk, and it\'s not the big one."',
          'Her description lets you draw the right thing on the first try.',
        ],
      },
    ],
  },
]

export const LANGUAGE_SKILLS: Skill[] = buildSkills('language', LANGUAGE_SEEDS)

// ─────────────────────────────────────────────────────────────
// Logic
//
// Her standout domain, so the ladders run high on purpose: class inclusion,
// three by three matrices, syllogisms with fantasy premises, and finally
// designing puzzles for other people. There is deliberately headroom above
// where she is now.
// ─────────────────────────────────────────────────────────────

const LOGIC_SEEDS: readonly SkillSeed[] = [
  {
    key: 'opposites',
    name: 'Opposites',
    description:
      'Holding both ends of a dimension in mind at once and producing the other end on demand.',
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'Play an opposite volley in the car: you say a word, she says the other end, back and forth.',
      'Use them in real moments. "This water is hot, so this one is..." and wait.',
      'Try a stretchy one such as empty, first or over and see how far she reaches.',
    ],
    ladder: [
      {
        descriptor:
          'Acts out a pair on request: up and down, in and out, stop and go.',
        exemplars: ['Goes up and down the step on the words.', 'Puts the block in and out of the cup on cue.'],
      },
      {
        descriptor:
          'Finishes a spoken opposite inside a familiar frame for three or four pairs.',
        exemplars: ['"Day and..." gets "night".', 'Fills in "big and little" from a song.'],
      },
      {
        descriptor:
          'Gives the opposite of common words with no frame and no choices offered.',
        exemplars: ['Answers big, hot, up, wet correctly.', 'Says "open" when asked the opposite of closed.'],
      },
      {
        descriptor:
          'Gives opposites for less obvious words and can name the dimension the pair belongs to.',
        exemplars: [
          'Handles heavy, empty, first, over.',
          'Says "that one\'s about size" after giving big and small.',
        ],
      },
      {
        descriptor:
          'Invents opposite pairs herself, including for feelings and actions, and notices when a proposed pair is not really opposite.',
        exemplars: [
          'Observed: generates opposites independently and unprompted.',
          'Offers brave and scared, build and break.',
        ],
      },
    ],
  },
  {
    key: 'causeEffect',
    name: 'Cause and Effect',
    description:
      'Linking what happened to what made it happen, and running the chain forward more than one step.',
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'At any spill or crash, ask what happened, then ask what happened next.',
      'Ask a backward question: "the floor is wet. What do you think happened here?"',
      'Once she gives you one cause, ask whether anything else could have done it.',
    ],
    ladder: [
      {
        descriptor:
          'Repeats an action to make the same thing happen again.',
        exemplars: ['Presses the button again for the sound.', 'Drops the spoon a second time to watch it fall.'],
      },
      {
        descriptor: 'Predicts the immediate result of one action before doing it.',
        exemplars: ['"If I let go it falls."', '"That will spill."'],
      },
      {
        descriptor: 'Explains a chain of two steps out loud.',
        exemplars: ['"I knocked it and it fell down."', '"It rained so the ground got wet."'],
      },
      {
        descriptor:
          'Chains three or four steps forward, and reasons backward from a result to a likely cause.',
        exemplars: [
          'Observed: builds 3 to 4 step cause and effect chains.',
          '"The floor is wet, so the cup must have tipped over."',
        ],
      },
      {
        descriptor:
          'Reasons about a cause she cannot see, offers more than one possible cause for the same result, and says how to tell which one it was.',
        exemplars: [
          '"Maybe the wind blew it, or maybe the cat did. Let\'s look for paw prints."',
          'Explains why the ice melted without seeing it happen.',
        ],
      },
    ],
  },
  {
    key: 'categories',
    name: 'Categories',
    description:
      'Grouping things by what they have in common, and inventing groups of her own.',
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'Tip out a toy bin and ask her to make groups, without telling her how.',
      'When she has sorted it once, ask her to do the whole thing a different way.',
      'Ask her to name each group she made.',
    ],
    ladder: [
      {
        descriptor:
          'Puts identical things together in one pile and leaves the rest where they are.',
        exemplars: ['Stacks all the same blocks together.', 'Matches a spoon to a spoon.'],
      },
      {
        descriptor:
          'Sorts a mixed set by one obvious feature such as color or shape.',
        exemplars: ['All the red ones here, all the blue ones there.', 'Sorts the buttons by size.'],
      },
      {
        descriptor:
          'Sorts by what things are rather than what they look like, and names the group.',
        exemplars: ['Puts animals in one pile and food in another.', 'Says "these are the vehicles".'],
      },
      {
        descriptor:
          'Invents her own grouping rule for a mixed set and states the rule out loud.',
        exemplars: [
          'Observed: creates her own groupings.',
          '"These all live in the water and these all live on land."',
        ],
      },
      {
        descriptor:
          'Re sorts the same set a second way when asked, and says what changed about the rule.',
        exemplars: [
          'Sorts by animal type, then again by big and small, and explains the switch.',
          'Notices an item that fits both groups and says so.',
        ],
      },
    ],
  },
  {
    key: 'oddOneOut',
    name: 'Odd One Out',
    description:
      'Finding the item that does not belong, and saying what rule the others share. Picking is the easy half; the reason is the skill.',
    typicalMonths: [36, 48],
    prerequisites: ['logic.categories'],
    assessmentHints: [
      'Put four things from the kitchen on the table, ask which one does not belong, then ask why.',
      'Build one with two defensible answers and let her argue for the one she picked.',
      'Let her set one up for you and see whether her rule holds.',
    ],
    ladder: [
      {
        descriptor:
          'Matches identical items and notices one that is plainly different from the rest.',
        exemplars: ['Spots the one blue block among five red ones.', 'Points at the odd sock.'],
      },
      {
        descriptor:
          'Picks the odd item from three when the difference is color, shape or size.',
        exemplars: ['Picks the big one from two small ones.', 'Finds the square among circles.'],
      },
      {
        descriptor:
          'Picks the odd item from four by what things are, not how they look.',
        exemplars: ['Picks the shoe from apple, banana, pear, shoe.', 'Picks the cat from three vehicles.'],
      },
      {
        descriptor:
          'Says the reason as well as the answer, naming what the others have in common.',
        exemplars: [
          'Observed: explains her reasoning rather than only pointing.',
          '"Those are all fruit and a shoe is for wearing."',
        ],
      },
      {
        descriptor:
          'Handles a set with more than one defensible answer, picks one and defends it.',
        exemplars: [
          '"You could say the ball because it\'s the only round one, or the sock because the rest are toys."',
          'Changes her answer when you give a better rule, and says why.',
        ],
      },
    ],
  },
  {
    key: 'seriation',
    name: 'Seriation',
    description:
      'Putting things in order along one dimension and slotting a new one into an order that already exists. Piaget\'s classic test of ordered thinking.',
    typicalMonths: [42, 54],
    prerequisites: [],
    assessmentHints: [
      'Line up five spoons or nesting cups and ask for smallest to biggest.',
      'When the row is done, hand her one more and ask where it goes.',
      'Try a dimension you cannot see: heaviest, oldest, loudest.',
    ],
    ladder: [
      {
        descriptor: 'Picks the bigger or smaller of two things.',
        exemplars: ['Takes the bigger cookie every time.', 'Points to the smaller shoe when asked.'],
      },
      {
        descriptor: 'Orders three objects by size from biggest to smallest.',
        exemplars: ['Lines up three bears in order.', 'Nests three cups correctly.'],
      },
      {
        descriptor:
          'Orders five objects by size and fixes the row when one is out of place.',
        exemplars: ['Puts five sticks in length order.', 'Spots and swaps the one that is wrong.'],
      },
      {
        descriptor:
          'Slots a new item into a finished order in the right place, without rebuilding the whole row.',
        exemplars: [
          'Puts the extra stick between the third and fourth.',
          'Compares only the two neighbors before placing it.',
        ],
      },
      {
        descriptor:
          'Orders by a dimension she cannot see, and reasons across the row: if A beats B and B beats C, then A beats C.',
        exemplars: [
          'Orders three bags by weight after holding each one.',
          '"The red one is heavier than the blue, and the blue is heavier than the green, so the red beats the green."',
        ],
      },
    ],
  },
  {
    key: 'analogies',
    name: 'Analogies',
    description:
      'Carrying a relationship from one pair to another: A goes with B the way C goes with what. Goswami showed children can do this well before school when the relations are familiar.',
    typicalMonths: [42, 54],
    prerequisites: ['logic.categories', 'logic.causeEffect'],
    assessmentHints: [
      'Start with real objects on the table: a bird goes with a nest, so a person goes with...?',
      'Rotate the relation type: part and whole, what it is for, who uses it, where it lives.',
      'After she answers, ask why the pair goes together. The reason is the level.',
    ],
    ladder: [
      {
        descriptor:
          'Matches a thing to the same thing, picture to picture or object to object.',
        exemplars: ['Puts the shoe card on the shoe card.', 'Finds the pair in a matching game.'],
      },
      {
        descriptor: 'Pairs two things that go together in real life.',
        exemplars: ['Sock with foot, cup with drink.', 'Puts the lid with the pot.'],
      },
      {
        descriptor:
          'Completes an analogy about what something is for or where it lives, with the objects or pictures present.',
        exemplars: [
          'Observed: bird goes with nest, so person goes with house.',
          'Dog goes with bone, so bird goes with worm.',
        ],
      },
      {
        descriptor:
          'Completes spoken analogies with nothing in front of her, across several relation types.',
        exemplars: [
          'Hand goes with glove, so foot goes with sock.',
          'Puppy goes with dog, so kitten goes with cat.',
        ],
      },
      {
        descriptor:
          'Explains the relationship itself and makes up an analogy of her own.',
        exemplars: [
          '"Because that\'s where they both sleep."',
          'Offers "a wheel is to a car like a leg is to a person".',
        ],
      },
    ],
  },
  {
    key: 'classificationHierarchy',
    name: 'Classification Hierarchy',
    description:
      'Understanding that a group sits inside a bigger group. A poodle is a dog and a dog is an animal, all at once.',
    typicalMonths: [54, 66],
    prerequisites: ['logic.categories'],
    assessmentHints: [
      'Ask up and then down: is a poodle a dog? Is a dog an animal? Now name another animal.',
      'Draw circles inside circles on paper and let her place toy animals in them.',
      'Try the big one when she is warm: are there more dogs or more animals in the world?',
    ],
    ladder: [
      {
        descriptor:
          'Names things at the everyday level, one label for one thing.',
        exemplars: ['Says dog, car, apple.', 'Calls every four legged animal a dog for a while.'],
      },
      {
        descriptor:
          'Puts a specific thing into its everyday group when asked directly.',
        exemplars: ['"Is a poodle a dog?" Yes.', '"Is a fire truck a truck?" Yes.'],
      },
      {
        descriptor:
          'Answers one level up and one level down: a dog is an animal, and an animal could be a horse.',
        exemplars: ['"Is a dog an animal?" Yes.', 'Names three animals when asked.'],
      },
      {
        descriptor:
          'Names the levels in order and gives examples at each one.',
        exemplars: [
          'Poodle, dog, animal, living thing, said in order.',
          'Gives two examples for every level when asked.',
        ],
      },
      {
        descriptor:
          'Answers a class inclusion question correctly and explains that the small group is inside the big one.',
        exemplars: [
          '"More animals, because all the dogs are animals too, and there are cats as well."',
          'Catches the trick in "are there more roses or more flowers?"',
        ],
      },
    ],
  },
  {
    key: 'conditionalReasoning',
    name: 'Conditional Reasoning',
    description:
      'Reasoning with if and then: forward from the condition, and the harder direction, backward from the result.',
    typicalMonths: [48, 60],
    prerequisites: ['logic.causeEffect'],
    assessmentHints: [
      'Ask an if question about the weather or the plan at breakfast.',
      'Ask the backward version: "the grass is wet. So what happened in the night?"',
      'Slip a not into the rule and see whether she slows down to work it out.',
    ],
    ladder: [
      {
        descriptor:
          'Follows a first and then instruction in the order it was given.',
        exemplars: ['"First shoes, then outside."', 'Waits for the first part before asking for the second.'],
      },
      {
        descriptor:
          'Predicts the result of a stated rule about the thing in front of her.',
        exemplars: ['"If you tip it, it spills."', '"If I push it, it goes down the ramp."'],
      },
      {
        descriptor:
          'Answers a forward if question about a situation that is not happening now.',
        exemplars: [
          'Observed: forward if chains work.',
          '"If it rains, the picnic gets wet so we stay inside."',
        ],
      },
      {
        descriptor:
          'Reasons backward from the result to the condition, and answers a what if not question.',
        exemplars: [
          '"The grass is wet, so it must have rained."',
          '"If we didn\'t have the umbrella we would get wet."',
        ],
      },
      {
        descriptor:
          'Handles a rule with a not or an exception in it, and notices when the rule does not settle the question.',
        exemplars: [
          '"If it\'s not raining we go outside. It\'s sunny, so we go."',
          '"That doesn\'t tell us. It could be either one."',
        ],
      },
    ],
  },
  {
    key: 'deduction',
    name: 'Deduction',
    description:
      'Narrowing to one answer by ruling the others out. Clue by clue, holding the earlier clues in mind while the new one arrives.',
    typicalMonths: [48, 60],
    prerequisites: ['logic.categories', 'logic.oddOneOut'],
    assessmentHints: [
      'Hide a snack and give clues one at a time, starting with two.',
      'After she solves it, ask which clue told her the answer. The trail matters more than the answer.',
      'Line her toys up as the suspects and let her ask the questions.',
    ],
    ladder: [
      {
        descriptor: 'Finds a hidden thing from one direct clue.',
        exemplars: ['"It\'s under the blue cup." Finds it.', 'Follows a point and a hint to the toy.'],
      },
      {
        descriptor:
          'Uses one clue that says what it is not, with two things to choose from.',
        exemplars: ['"It\'s not the red one." Picks the blue.', 'Rules out the cup she already looked under.'],
      },
      {
        descriptor:
          'Solves a puzzle with two clues from four choices, with the clues repeated and the items in front of her.',
        exemplars: ['"It\'s not red and it\'s round." Picks the blue ball.', 'Points at the ones each clue removes.'],
      },
      {
        descriptor:
          'Solves a three clue puzzle, keeping the earlier clues in mind, with occasional reminders.',
        exemplars: [
          'Observed: solves three clue puzzles with support.',
          'Asks you to say clue one again, then finishes it.',
        ],
      },
      {
        descriptor:
          'Solves a four clue puzzle on her own and says out loud which clue removed which option.',
        exemplars: [
          '"Not the cat because it doesn\'t swim, not the fish because it has legs..."',
          'Finishes without the items in front of her.',
        ],
      },
    ],
  },
  {
    key: 'matrixReasoning',
    name: 'Matrix Reasoning',
    description:
      'Working out the missing cell of a grid by reading the rule across the rows and down the columns at the same time. The Raven style task, in objects first.',
    typicalMonths: [54, 66],
    prerequisites: ['logic.categories', 'logic.seriation'],
    assessmentHints: [
      'Lay out a two by two grid of real objects with one gap and ask what belongs in the hole.',
      'Ask her to say the rule for the row and then the rule for the column.',
      'Let her build a grid for you and leave the gap herself.',
    ],
    ladder: [
      {
        descriptor: 'Places a shape into its matching outline or hole.',
        exemplars: ['Completes a shape sorter.', 'Puts the puzzle piece in the matching space.'],
      },
      {
        descriptor:
          'Finishes a single row where one feature changes and one gap is left.',
        exemplars: ['Red, red, red, then the gap.', 'Big, small, big, then the gap.'],
      },
      {
        descriptor:
          'Completes a two by two grid where one feature changes across and a different one changes down.',
        exemplars: [
          'Observed: two by two matrices solved.',
          'Fills the fourth cell of color across, size down.',
        ],
      },
      {
        descriptor:
          'Completes a three by three grid with one changing feature and checks the answer in both directions.',
        exemplars: [
          'Finishes a three by three of growing circles.',
          'Looks along the row and then down the column before choosing.',
        ],
      },
      {
        descriptor:
          'Completes a three by three where two features change at once, and states the rule.',
        exemplars: [
          '"It gets one bigger going that way and it turns going down."',
          'Rejects a near miss answer and says exactly what is wrong with it.',
        ],
      },
    ],
  },
  {
    key: 'absurdityDetection',
    name: 'Absurdity Detection',
    description:
      'Noticing when a statement or a picture contradicts how the world works, and saying what is wrong with it. It needs a model of the world solid enough to be violated.',
    typicalMonths: [42, 54],
    prerequisites: ['logic.causeEffect'],
    assessmentHints: [
      'Drop a silly sentence into normal conversation with a straight face and see who catches it.',
      'Once she catches it, ask her to fix it.',
      'Ask her to make one up for you, then act fooled.',
    ],
    ladder: [
      {
        descriptor: 'Laughs at an obvious joke error she can see.',
        exemplars: ['Giggles when you put a shoe on your head.', 'Laughs when you call the dog a cow.'],
      },
      {
        descriptor:
          'Says no and corrects a wrong statement about something in front of her.',
        exemplars: ['"That\'s not a hat, that\'s a cup!"', 'Corrects you when you name the wrong color.'],
      },
      {
        descriptor:
          'Catches an impossibility she only hears, with nothing to look at, and calls it silly.',
        exemplars: [
          'Observed: catches "fish fly in the sky".',
          'Laughs at "I ate my bicycle for breakfast".',
        ],
      },
      {
        descriptor:
          'Says what is wrong and how it should be instead.',
        exemplars: [
          '"Fish swim in the water. Birds fly in the sky."',
          '"You can\'t eat a bicycle, it\'s metal. You eat toast."',
        ],
      },
      {
        descriptor:
          'Catches an absurdity that needs two facts put together, explains the conflict, and invents her own for others to catch.',
        exemplars: [
          '"He can\'t wear a swimsuit to shovel snow, he would be freezing."',
          'Makes up "the sun came out so we put our coats on" and waits for you to notice.',
        ],
      },
    ],
  },
  {
    key: 'syllogisms',
    name: 'Syllogisms',
    description:
      'Drawing the conclusion two statements force, even when the statements are made up. Hawkins and colleagues found young children reason well from fantasy premises when it is framed as a game.',
    typicalMonths: [54, 66],
    prerequisites: ['logic.classificationHierarchy', 'logic.conditionalReasoning'],
    assessmentHints: [
      'Start with true premises about a pet or a character she loves.',
      'Then try a silly one, all bears wear hats, and see whether she stays inside the game instead of correcting you.',
      'Offer a case the statements do not settle and see whether she says we cannot know.',
    ],
    ladder: [
      {
        descriptor:
          'Judges a single statement about the room true or false.',
        exemplars: ['"The cup is on the table." Yes.', 'Says no when you claim the light is off.'],
      },
      {
        descriptor: 'Judges a single statement about a familiar fact true or false.',
        exemplars: ['"Dogs bark." Yes.', '"Cars swim." No.'],
      },
      {
        descriptor:
          'Draws the conclusion from two true statements given together.',
        exemplars: [
          '"All cats have whiskers. Milo is a cat. So Milo has whiskers."',
          'Answers without needing the animal in front of her.',
        ],
      },
      {
        descriptor:
          'Draws the conclusion from made up statements she knows are untrue, staying inside the game.',
        exemplars: [
          '"All bears wear hats. This is a bear. So it wears a hat."',
          'Says "in the game they do" instead of correcting the premise.',
        ],
      },
      {
        descriptor:
          'Says when two statements do not settle the question, instead of guessing an answer.',
        exemplars: [
          '"Some birds fly. This is a bird. We can\'t know if it flies."',
          'Asks for another clue rather than picking.',
        ],
      },
    ],
  },
  {
    key: 'puzzleDesign',
    name: 'Puzzle Design',
    description:
      'Making a problem for somebody else to solve and judging whether it works. Building the puzzle proves she holds the rule, not just the answer, and teaching it is where understanding consolidates.',
    typicalMonths: [60, 72],
    prerequisites: ['logic.oddOneOut', 'logic.deduction', 'logic.categories'],
    assessmentHints: [
      'Right after she solves a riddle, ask her to make one for the other parent.',
      'Play stuck and ask her for a hint. Notice whether the hint actually helps.',
      'Ask whether her puzzle has one answer or several, and how she knows.',
    ],
    ladder: [
      {
        descriptor:
          'Hides something and enjoys the game of watching somebody else look for it.',
        exemplars: ['Hides the toy and shrieks when you look.', 'Covers her eyes and waits to be found.'],
      },
      {
        descriptor: 'Hides something and gives one clue about where it is.',
        exemplars: ['"It\'s in the kitchen."', '"You\'re getting warmer."'],
      },
      {
        descriptor:
          'Makes up a riddle or an odd one out for an adult, with a rule that mostly holds together.',
        exemplars: ['"Which one doesn\'t belong: spoon, fork, dog?"', 'Invents a what am I riddle about a cat.'],
      },
      {
        descriptor:
          'Builds a puzzle with exactly one right answer and checks the solver\'s reasoning, not only their answer.',
        exemplars: [
          'Asks "but why did you pick that one?" after you answer.',
          'Fixes her own puzzle when you find a second valid answer.',
        ],
      },
      {
        descriptor:
          'Pitches the puzzle at the person solving it, offers a hint when they are stuck, and explains the rule afterwards.',
        exemplars: [
          'Makes an easier one for a younger cousin and a harder one for a parent.',
          'Gives a hint that narrows it without giving it away, then explains the trick.',
        ],
      },
    ],
  },
]

export const LOGIC_SKILLS: Skill[] = buildSkills('logic', LOGIC_SEEDS)

// ─────────────────────────────────────────────────────────────
// Combined export
// ─────────────────────────────────────────────────────────────

export const LITERACY_LANGUAGE_LOGIC_SKILLS: Skill[] = [
  ...LITERACY_SKILLS,
  ...LANGUAGE_SKILLS,
  ...LOGIC_SKILLS,
]
